import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  BenchmarkStatus,
  BenchmarkStatusDefaultColors,
  BenchmarkStatusDisplayedNames,
  ColorCriteria,
  EvidenceAssessment,
  EvidenceData,
  Measurement,
  measurementOptionsList,
  MeasurementType,
  MeasurementTypeDisplayedNames,
} from "../../../../../store/onboarding/types";

// Extend ColorCriteria to allow string indexing for custom_color_criteria
type CustomColorCriteria = {
  [grade: string]: {
    [time_period: string]: ColorCriteria[];
  };
};
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import EditMeasureColorCriteriaModal from "../../../color-picker/EditMeasureColorCriteriaModal";
import { ApplicationState } from "../../../../../store";
import { bindActionCreators, Dispatch } from "redux";
import {
  createAssessment,
  getEvidenceAssessments,
  hideAddAssessmentModal,
  hideAddProgressMonitorModal,
  openAddAssessmentModal,
  updateAssessment,

} from "../../../../../store/onboarding/actions";
import { connect, useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle, faLock, faPlusCircle, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { Col, Form, OverlayTrigger, Popover, Spinner, Table, Tooltip } from "react-bootstrap";
import EditPossibleValuesModal from "./EditPossibleValuesModal";
import { useLoading } from "../../../../../utils/hooks/useLoading";
import ReadingSkillSelector from "./ReadingSkillSelector";
import { getSkillFocuses } from "../../../../../store/groups/actions";
import { CasesState, GoalTypes } from "../../../../../store/onboarding/cases/types";
import {
  getTimePeriodSortLabel,
  Grade,
  SkillFocusArea,
  TimePeriod,
} from "../../../../../store/groups/types";
import _, { isObject } from "lodash";
import { toastr } from "react-redux-toastr";
import { showConfirmDialog } from "../../../../../store/confirm-dialog/actions";
import useDeviceDetect from "../../../../../utils/hooks/useDeviceDetect";
import { UserInfo } from "../../../../../store/auth/types";

type PropsFromState = {
  showModal: boolean;
  enterDataManuallyType?: GoalTypes;
  isNewProgressMonitoring?: boolean;
  isRedirectToSetGoal?: boolean;
  evidenceAssessment?: EvidenceAssessment;
  mode?: 'create' | 'edit' | 'view';
  fromPage?: string;
  isLoading: {
    createAssessment: boolean;
    updateAssessment: boolean;
  };
  errors: {
    createAssessment?: string;
    updateAssessment?: string;
  };
};

type DispatchProps = {
  hideAddAssessmentModal: () => any;
  hideAddProgressMonitorModal: () => any;
  openAddAssessmentModal: () => any;
  createAssessment: (assessment: Partial<EvidenceData>, isGeneralOutcomeMeasure?: boolean) => any;
  updateAssessment: (assessmentId: number, assessmentInfo: any) => any;
};

type Props = PropsFromState & DispatchProps;

const MAX_LENGTH_TEXT_INPUT = 35;

const CreateAssessmentModal: React.FC<Props> = (props) => {
  const dispatch = useDispatch();

  const currentUser: UserInfo = useSelector(
    (s: ApplicationState) => s.auth.userInfo!
  );

  const dataColumnFormEl = useRef<HTMLFormElement>(null);
  const [useGradeSpecificColor, setUseGradeSpecificColor] = useState<boolean>(
    false
  );
  const [withReadingAlgorithm, setWithReadingAlgorithm] = useState<boolean>(
    false
  );

  const tabName: string = useSelector<ApplicationState, string>((s) => s.cases.tabName);

  const supportedGrades: Grade[] = useMemo(() => {
    return ["K", "G1", "G2", "G3", "G4", "G5", "G6", "G7", "G8", "G9", "G10", "G11", "G12"];
  }, []);
  const timePeriods = useMemo(() => {
    return Object.values(TimePeriod);
  }, []);
  const benchmarkStatuses = useMemo(() => {
    return Object.values(BenchmarkStatus);
  }, []);

  const raBenchmarkForNumericType = useMemo(() => {
    const colorCriteriaList: ColorCriteria[] = [];
    supportedGrades.forEach((grade) => {
      timePeriods.forEach((timePeriod) => {
        let orderKey = 1;
        benchmarkStatuses.forEach((benchmarkStatus) => {
          const colorCriteria: ColorCriteria = {
            display_name: BenchmarkStatusDisplayedNames[benchmarkStatus],
            color: BenchmarkStatusDefaultColors[benchmarkStatus],
            grade: grade,
            time_period: timePeriod,
            benchmark_status: benchmarkStatus,
            isVisible: true,
            order: orderKey++
          };
          colorCriteriaList.push(colorCriteria);
        });
      });
    });

    return colorCriteriaList;
  }, [supportedGrades, timePeriods, benchmarkStatuses]);

  const raBenchmarkForCategoryType: Pick<
    Measurement,
    "possible_values" | "color_criteria" | "type"
  > = useMemo(() => {
    return {
      possible_values: benchmarkStatuses.map((benchmarkStatus, index) => ({
        display_name: BenchmarkStatusDisplayedNames[benchmarkStatus],
        order: index + 1,
      })),
      color_criteria: benchmarkStatuses.map((benchmarkStatus, index) => ({
        order: index + 1,
        selected_value_order: index + 1,
        display_name: BenchmarkStatusDisplayedNames[benchmarkStatus],
        color: BenchmarkStatusDefaultColors[benchmarkStatus],
      })),
    };
  }, [benchmarkStatuses]);

  const newMeasurement: Measurement = useMemo(
    () => ({
      display_name: "",
      column_index: 0,
      column_name: "",
      type: MeasurementType.NUMBER,
      reading_skill: undefined,
      color_criteria: useGradeSpecificColor
        ? raBenchmarkForNumericType
        : undefined,
      unique_id: Date.now(),
      isAddNew: true,
    }),
    [useGradeSpecificColor]
  );

  const [assessmentName, setAssessmentName] = useState<string>("");
  const [isAssessmentEditMode, setIsAssessmentEditMode] = useState<boolean>(false);
  const [studentIdColumnHeader, setStudentIdColumnHeader] = useState<string>(
    ""
  );
  const [studentFNameColumnHeader, setStudentFNameColumnHeader] = useState<
    string
  >("");
  const [studentLNameColumnHeader, setStudentLNameColumnHeader] = useState<
    string
  >("");
  const [studentTeacherColumnHeader, setStudentTeacherColumnHeader] = useState<
    string
  >("");
  const [studentGradeColumnHeader, setStudentGradeColumnHeader] = useState<
    string
  >("");
  const [studentRaceColumnHeader, setStudentRaceColumnHeader] = useState<
    string
  >("");
  const [measurements, setMeasurements] = useState<Measurement[]>([]);
  const [editMeasureColorIndex, setEditMeasureColorIndex] = useState<number>(0);
  const [
    showEditMeasureColorCriteriaModal,
    setShowEditMeasureColorCriteriaModal,
  ] = useState<boolean>(false);
  const [
    showEditPossibleValuesModal,
    setShowEditPossibleValuesModal,
  ] = useState<boolean>(false);
  const [selectedMeasurement, setSelectedMeasurement] = useState<
    Measurement | undefined
  >(undefined);
  // Snapshot ref to allow restoring measurements/searchFilter when the color criteria modal is cancelled
  const prevModalStateRef = useRef<{
    measurements: Measurement[];
    searchFilterEntry?: any;
  } | null>(null);

  const gradeOrder = ["K", ...Array.from({ length: 12 }, (_, i) => `G${i + 1}`)];
  let initialSearchObj = {
    filterIndex: 0,
    filterGrade: [],
    filterAdditionalBenchmark: true,
    ...gradeOrder.reduce((acc, grade) => {
      acc[`selectFor${grade}`] = [];
      return acc;
    }, {} as any),
  };

  let [searchFilter, setSearchFilter] = useState<any>({});

  const { isDesktop } = useDeviceDetect();

  useLoading(
    props.isLoading.createAssessment,
    props.errors.createAssessment,
    () => {
      props.hideAddAssessmentModal();
      setIsAssessmentEditMode(false);
      setSearchFilter({});
    },
    () => props.hideAddProgressMonitorModal()
  );

  useLoading(
    props.isLoading.updateAssessment,
    props.errors.updateAssessment,
    () => {
      props.hideAddAssessmentModal();
      setIsAssessmentEditMode(false);
      setSearchFilter({});
    },
    () => props.hideAddProgressMonitorModal()
  );

  const basicColumnsHeadersSelected = useMemo(() => {
    return studentIdColumnHeader &&
    studentFNameColumnHeader &&
    studentLNameColumnHeader;
  },[])

  const addOrder = (
    criteriaList: ColorCriteria[],
    useGradeSpecificColorCriteria: boolean
  ): ColorCriteria[] => {
    const totalCount = criteriaList.length;

    return criteriaList.map((criteria, index) => {
      if (Object.hasOwn(criteria, "order")) {
        return criteria;
      }

      let order = index + 1;

      if (
        useGradeSpecificColorCriteria &&
        totalCount === 3 &&
        index === totalCount - 1
      ) {
        order = 4;
      }

      return {
        ...criteria,
        order,
      };
    });
  };


  const transformCustomColorCriteria = (custom_color_criteria: CustomColorCriteria | ColorCriteria[] | undefined) => {
    if (custom_color_criteria && isObject(custom_color_criteria) && !Array.isArray(custom_color_criteria)) {
      let colorCriteria: ColorCriteria[] = [];
      for (const grade in custom_color_criteria) {
        if (Object.prototype.hasOwnProperty.call(custom_color_criteria, grade)) {
          const timePeriodByGrade = custom_color_criteria[grade];
          for (const time_period in timePeriodByGrade) {
            if (Object.prototype.hasOwnProperty.call(timePeriodByGrade, time_period)) {
              const colorCriteriaList = (timePeriodByGrade as { [key: string]: ColorCriteria[] })[time_period];
              if (Array.isArray(colorCriteriaList)) {
                colorCriteria = [
                  ...colorCriteria,
                  ...colorCriteriaList
                ];
              }
            }
          }
        }
      }
      const criteria = addOrder(
        colorCriteria,
        !!props.evidenceAssessment?.use_grade_specific_color_criteria
      )
      return criteria;
    }
    // If it's already an array, just return it
    return Array.isArray(custom_color_criteria) 
      ? addOrder(
        custom_color_criteria,
        !!props.evidenceAssessment?.use_grade_specific_color_criteria
      ) : [];
  }

  const updatedCustomColorCriteria = (colorCriteriaArr: ColorCriteria[]) => {
    if (!props.evidenceAssessment?.use_grade_specific_color_criteria) {
      return colorCriteriaArr || [];
    }
    const getLookupKey = (item: ColorCriteria) =>
      `${item.grade}_${item.time_period}_${item.order}`;
    // API records lookup
    const customMap = new Map(
      (colorCriteriaArr || []).map(item => [getLookupKey(item), item])
    );
    // Merge API values into RA template while preserving RA sequence
    const mergedFullList = (raBenchmarkForNumericType || []).map(raItem => ({
      ...raItem,
      ...customMap.get(getLookupKey(raItem)),
    }));
    const gradeList = ["K", ...Array.from({ length: 12 }, (_, i) => `G${i + 1}`)];
    const selectedTimePeriods = new Set(
      gradeList.flatMap(
        grade =>
          (searchFilter[`selectFor${grade}`] || []).map(
            (tp: any) => `${tp.grade}_${tp.value}`
          )
      )
    );

    const hasCustomCriteria = customMap.size > 0;

    return mergedFullList.map(item => {
      const lookupKey = getLookupKey(item);
      const isCustom = customMap.has(lookupKey);

      const visible =
        isCustom ||
        !hasCustomCriteria ||
        selectedTimePeriods.has(`${item.grade}_${item.time_period}`);

      return {
        ...item,
        isVisible: visible,
        range_min: visible ? item.range_min : undefined,
        range_max: visible ? item.range_max : undefined,
      };
    });
  };

  const handleModalShow = () => {
    if (props.evidenceAssessment && (props.mode === 'edit' || props.mode === 'view')) {
      setAssessmentName(props.evidenceAssessment.name);
      setUseGradeSpecificColor(props.evidenceAssessment.use_grade_specific_color_criteria!);
      setWithReadingAlgorithm(props.evidenceAssessment.with_reading_algorithm!);
      setIsAssessmentEditMode(true);
      let measurementData = props.evidenceAssessment?.measurements
        ?.map((measure, index) => ({
            ...measure,
            display_name: measure.display_name || "",
            column_index: index,
            column_name: measure.column_name || "",
            type: MeasurementType.NUMBER,
            reading_skill: measure.skill_areas?.[0] || "",
            color_criteria: (props.mode == 'edit') 
                            ? updatedCustomColorCriteria(
                                transformCustomColorCriteria(
                                  measure.custom_color_criteria
                                )
                              )
                            : transformCustomColorCriteria(
                                (measure.color_criteria?.length
                                  ? measure.color_criteria
                                  : measure.custom_color_criteria) || []
                              ),
            unique_id: Date.now(),
              isAddNew: false,
            })
          )
      setMeasurements(measurementData || []);
    } else {
      setSearchFilter({});
      setAssessmentName("");
      setStudentIdColumnHeader("Student ID");
      setStudentFNameColumnHeader("First Name");
      setStudentLNameColumnHeader("Last Name");
      setMeasurements([newMeasurement]);
      setShowEditMeasureColorCriteriaModal(false);
      setShowEditPossibleValuesModal(false);
      setSelectedMeasurement(undefined);
      (props.enterDataManuallyType == 'academic') && refreshColorCriteria(true);
      setUseGradeSpecificColor(true);
      setWithReadingAlgorithm(false);
      setIsAssessmentEditMode(false);
    }
  };

  useEffect(() => {
    if (useGradeSpecificColor) {
      dispatch(getSkillFocuses());
    }
  }, [useGradeSpecificColor]);

  const handleAssessmentNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;
    if (value.length <= MAX_LENGTH_TEXT_INPUT) {
      setAssessmentName(value);
    }
  };

  // const handleStudentIdColumnHeaderChange = (
  //   event: React.ChangeEvent<HTMLInputElement>
  // ) => {
  //   const { value } = event.target;
  //   setStudentIdColumnHeader(value);
  // };

  // const handleStudentFNameColumnHeaderChange = (
  //   event: React.ChangeEvent<HTMLInputElement>
  // ) => {
  //   const { value } = event.target;
  //   setStudentFNameColumnHeader(value);
  // };

  // const handleStudentLNameColumnHeaderChange = (
  //   event: React.ChangeEvent<HTMLInputElement>
  // ) => {
  //   const { value } = event.target;
  //   if(value == studentFNameColumnHeader){
  //     document.getElementById('studentLNameColumnHeader')?.style.setProperty('border', '1px solid red');
  //   } else {
  //     document.getElementById('studentLNameColumnHeader')?.style.removeProperty('border');
  //   }
  //     setStudentLNameColumnHeader(value);
  // };

  // const handleStudentTeacherColumnHeaderChange = (
  //   event: React.ChangeEvent<HTMLInputElement>
  // ) => {
  //   const { value } = event.target;
  //   setStudentTeacherColumnHeader(value);
  // };

  // const handleStudentGradeColumnHeaderChange = (
  //   event: React.ChangeEvent<HTMLInputElement>
  // ) => {
  //   const { value } = event.target;
  //   setStudentGradeColumnHeader(value);
  // };

  // const handleStudentRaceColumnHeaderChange = (
  //   event: React.ChangeEvent<HTMLInputElement>
  // ) => {
  //   const { value } = event.target;
  //   setStudentRaceColumnHeader(value);
  // };

  const handleMeasurementAdd = (event: React.FormEvent<any>) => {
    event.preventDefault();
    setMeasurements((measurements) => [...measurements, {
      ...newMeasurement, unique_id: Date.now(), isAddNew: true
    }]);
  };

  const getDefaultTypeValues = (
    measureType: MeasurementType
  ): Partial<Measurement> => {
    let defaultValues: Partial<Measurement>;
    switch (measureType) {
      case MeasurementType.NUMBER:
        defaultValues = {
          range_min: 200,
          range_max: 800,
          possible_values: undefined,
          color_criteria: useGradeSpecificColor
            ? raBenchmarkForNumericType
            : undefined,
        };
        break;
      case MeasurementType.PERCENTAGE:
        defaultValues = {
          range_min: 0,
          range_max: 100,
          possible_values: undefined,
          color_criteria: useGradeSpecificColor
            ? raBenchmarkForNumericType
            : undefined,
        };
        break;
      case MeasurementType.CATEGORY:
        defaultValues = {
          range_min: undefined,
          range_max: undefined,
          ...raBenchmarkForCategoryType,
        };
        break;
      default:
        defaultValues = {};
        break;
    }
    return defaultValues;
  };

  const handleMeasurementChange = (changingIndex: number, unique_id?: number) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = event.target;
    setSearchFilter((prev: any) => {
      if (["type", "range_min", "range_max"].includes(name)) {
        return {
          ...prev,
          [String(unique_id)]: {
            ...initialSearchObj,
            filterIndex: unique_id, // optional but usually correct
          },
        };
      }
      return prev
    });

    setMeasurements((measurements) => {
      return measurements.map((measurement: Measurement, index: number) => {
        if (index === changingIndex) {
          if (name === "type") {
            const defTypeValues = getDefaultTypeValues(+value);
            return {
              ...measurement,
              ...defTypeValues,
              type: +value,
            };
          }
          if (["range_min", "range_max"].includes(name)) {
            // Check if the value is different from the previous value
            const prevValue = measurement[name as keyof Measurement];
            const newValue = value === "" ? undefined : +value;
            if (prevValue !== newValue) {
              // If changed, reload color_criteria with all visible records
              let updatedColorCriteria = measurement.color_criteria;
              if (useGradeSpecificColor) {
                updatedColorCriteria = raBenchmarkForNumericType.map(cc => ({
                  ...cc,
                  [name]: undefined,
                  isVisible: true,
                }));
              } else if (measurement.type === MeasurementType.NUMBER 
                || measurement.type === MeasurementType.PERCENTAGE) {
                  updatedColorCriteria = undefined
                  // updatedColorCriteria = [
                  //   ...(measurement.color_criteria?.map(cc => ({
                  //     ...cc,
                  //     [name]: undefined,
                  //     isVisible: true,
                  //   })) || []),
                  // ];
              }
              return {
                ...measurement,
                [name]: newValue,
                color_criteria: updatedColorCriteria,
              };
            }
          }
          return {
            ...measurement,
            [name]: value,
          }
        }
        return measurement;
      });
    });
  };

  const handleMeasurementRemove = (measurement: Measurement) => () => {
    const confirmAndRemove = () => {
      setSearchFilter((prevFilter:any) => {
        const newFilter = {...prevFilter};
        delete newFilter[measurement.unique_id!];
        return newFilter;
      });
      setMeasurements((measurements) => {
        return measurements.filter(
          (m: Measurement) => m.unique_id !== measurement.unique_id
        );
      })
    };
    if(measurement.color_criteria
      ?.some((c) =>
        c.range_min != undefined
        && c.range_max != undefined
      )) {
        dispatch(
          showConfirmDialog({
            title: "Remove Data Column",
            text: (
              <>
                <p className="text-center font-weight-bold">Are you sure you want to remove this data column?</p>
                <p className="text-center">This action cannot be undone.</p>
              </>
            ),
            onConfirm: () => confirmAndRemove(),
            confirmButtonText: "REMOVE",
          })
        );
      } else {
        confirmAndRemove();
      }
    };

  const createRangeTd = (measurement: Measurement, index: number) => {
    switch (measurement.type) {
      case MeasurementType.NUMBER:
        return (
          <div className={"form-inline justify-content-center"}>
            <div>
              <input
                required
                type="number"
                name="range_min"
                placeholder="Min"
                className="inputSm"
                value={(measurement.range_min != undefined) ? measurement.range_min : ""}
                onChange={handleMeasurementChange(index, measurement.unique_id)}
                disabled={props.mode === 'view' || props.mode === 'edit'}
              />
            </div>
            <strong>&nbsp;-&nbsp;</strong>
            <div>
              <input
                required
                type="number"
                name="range_max"
                placeholder="Max"
                className="inputSm"
                value={(measurement.range_max != undefined ? measurement.range_max : "")}
                onChange={handleMeasurementChange(index, measurement.unique_id)}
                disabled={props.mode === 'view' || props.mode === 'edit'}
              />
            </div>
          </div>
        );
      case MeasurementType.PERCENTAGE:
        return <div className="rangeDiv"> 0 - 100 </div>;
      case MeasurementType.CATEGORY:
      default:
        return (
          <Button
            disabled={useGradeSpecificColor}
            variant="primary"
            size="sm"
            onClick={() => {
              setSelectedMeasurement({...measurement, column_index: index});
              setShowEditPossibleValuesModal(true);
            }}
          >
            <div className="rangeDiv" style={{ cursor: "pointer" }}>
              Select
            </div>
          </Button>
        );
    }
  };

  const handleCreateAssessment = (event: React.SyntheticEvent) => {
    event.preventDefault();
    // if(isHeaderNameSame) {
    //   toastr.error("Unable to create assessment", "Student column names must be different.");
    //   return;
    // }
    if (withReadingAlgorithm && (props.enterDataManuallyType == 'academic') && measurements.length) {
      const readingSkills = measurements.map(m => m.reading_skill);
      const hasEmpty = readingSkills.some(skill => !skill);
      const hasDuplicates = new Set(readingSkills.filter(Boolean)).size !== readingSkills.filter(Boolean).length;
      if (hasEmpty) {
      toastr.error("Alert", "Measurement(s) must have a Reading Area.");
      return;
      }
      if (hasDuplicates) {
      toastr.error("Alert", "Measurement(s) must have a unique Reading Area.");
      return;
      }
    }
    const measurementArray = [...measurements.map((measurement)=> {
      return {
        ...measurement,
        color_criteria: measurement.color_criteria
        ?.filter((data: ColorCriteria) => (data.range_max !== undefined) || (data.range_min !== undefined)
        ) || [],
      }
    })];

    let isScoreRangeExist: boolean = true;
    for (let i = 0; i < measurementArray.length; i++) {
      const measurement = measurementArray[i];
      if(!measurement.color_criteria?.length){
        isScoreRangeExist = false;
        break;
      }
    }

    if (dataColumnFormEl.current?.reportValidity() && isScoreRangeExist) {
     
      const assessment: Partial<EvidenceData> = {
        first_name_column: studentFNameColumnHeader,
        last_name_column: studentLNameColumnHeader,
        unique_id_column: studentIdColumnHeader,
        teacher_column: studentTeacherColumnHeader,
        grade_column: studentGradeColumnHeader,
        race_column: studentRaceColumnHeader,
        assessment: {
          with_reading_algorithm: withReadingAlgorithm,
          use_grade_specific_color_criteria: useGradeSpecificColor,
          is_behavior: props.enterDataManuallyType == "academic" ? false : true,
          name: assessmentName,
          data_columns: useGradeSpecificColor
            ? measurements.map((m) => ({
                ...m,
                color_criteria: [],
                grade_specific_color_criteria: _.chain(m.color_criteria || [])
                  .filter((data: ColorCriteria) => (data.range_max !== undefined) || (data.range_min !== undefined))
                  .groupBy((cc) => cc.grade)
                  .mapValues((cList) =>
                    _.chain(cList)
                      .groupBy((cc) => cc.time_period)
                      .value()
                  )
                  .value(),
                reading_skill: withReadingAlgorithm ? m.reading_skill : undefined
              }))
            : measurements.map((m) => {
              if(!useGradeSpecificColor && !withReadingAlgorithm) {
                delete m.reading_skill
              }
              return m;
            }),
        },
      };
      props.createAssessment(assessment);
    } else {       
      toastr.error("Unable to create assessment", "Color criteria(s) missing for one of the Data Column(s)");   
      return; 
    }
  };

  const handleUpdateEvidenceAssessment = (event: React.SyntheticEvent) => {
    event.preventDefault();
    const assessmentInfo: any = {
      assessment_name: assessmentName,
      data_columns: useGradeSpecificColor
          ? measurements.map((m) => ({
              ...m,
              color_criteria: [],
              grade_specific_color_criteria: _.chain(m.color_criteria || [])
                .filter((data: ColorCriteria) => (data.range_max !== undefined) || (data.range_min !== undefined))
                .groupBy((cc) => cc.grade)
                .mapValues((cList) =>
                  _.chain(cList)
                    .groupBy((cc) => cc.time_period)
                    .value()
                )
                .value(),
              reading_skill: withReadingAlgorithm ? m.reading_skill : undefined
            }))
          : measurements.map((m) => {
            if(!useGradeSpecificColor && !withReadingAlgorithm) {
              delete m.reading_skill
            }
            m = {
              ...m,
              color_criteria: [],
              custom_color_criteria: m.color_criteria
            }
            return m;
          })
    };

    props.updateAssessment(props.evidenceAssessment?.id!, assessmentInfo);

  };

  const handleCreateProgressMonitoringAssessment = (event: React.SyntheticEvent) => {
    event.preventDefault();

    const assessment: Partial<EvidenceData> = {
      first_name_column: 'first_name_column',
      last_name_column: 'last_name_column',
      unique_id_column: 'unique_id_column',
      // teacher_column: 'teacher_column',
      // grade_column: 'grade_column',
      // race_column: 'race_column',
      assessment: {
        with_reading_algorithm: false,
        use_grade_specific_color_criteria: false,
        is_behavior: tabName == 'groups/behavior' ? true : false,
        name: assessmentName,
        data_columns:  [
          {
              display_name: assessmentName,
              column_index: 0,
              column_name: assessmentName,
              type: 0,
              color_criteria: [
                  {
                      benchmark_status: "below",
                      display_name: assessmentName,
                      range_min: 1,
                      range_max: 100,
                      order: 1,
                      color: "#ffd1da",
                      user_id: currentUser!.id
                  }
              ],
              range_min: 1,
              range_max: 100
          }
      ],
      },
    };

    props.createAssessment(assessment);

  };

  const handleHideAddAssessmentModal = () => {
    if((props.mode === 'view' || props.mode === 'edit') && (props.fromPage === 'searchForYourAssessmentModal')) {
      props.hideAddAssessmentModal();
    } else {
      if (!props.isLoading.createAssessment || !props.isLoading.updateAssessment) {
        if (props.isNewProgressMonitoring) {
          if (props.isRedirectToSetGoal) {
            dispatch(getEvidenceAssessments());
          }
          props.hideAddProgressMonitorModal();
          setIsAssessmentEditMode(false);
        } else {
          // Immediately hide the modal state without delay to prevent flicker
          props.hideAddAssessmentModal();
          setIsAssessmentEditMode(false);
          setSearchFilter({});
        }
      }
    }
  };

  const handleApplyColorCriteriaChanges = (updatedMeasurement: Measurement, searchFilter: any) => {
    setShowEditMeasureColorCriteriaModal(false);
    setSelectedMeasurement(undefined);
    let measurementArray: Measurement[] = measurements;
    if(useGradeSpecificColor) {
      let mergedTimePeriod = [
        ...["K", ...Array.from({ length: 12 }, (_, i) => `G${i + 1}`)]
        .flatMap((grade) => searchFilter[`selectFor${grade}`] || [])
      ];
  
      measurementArray = [...measurementArray.map(
        (measurement: Measurement): Measurement => {
          if ((measurement.unique_id === updatedMeasurement.unique_id)) {
            measurement =  {
              ...measurement,
              isAddNew: false,
              color_criteria: updatedMeasurement.color_criteria
                ?.map((colorCriteria: ColorCriteria) => {
                  let isVisibleCheck = mergedTimePeriod.some(
                        (timePeriod: any) =>
                          timePeriod.grade == colorCriteria.grade
                        && timePeriod.value == colorCriteria.time_period
                        && (colorCriteria.range_min != undefined)
                        && (colorCriteria.range_max != undefined)
                        && colorCriteria.isVisible
                      );
                  return ({
                    ...colorCriteria,
                    range_min:  isVisibleCheck ? colorCriteria.range_min : undefined,
                    range_max:  isVisibleCheck ? colorCriteria.range_max : undefined,
                    isVisible: isVisibleCheck
                  });
                })
            }
          }
          return measurement;
        }
      )]
      setSearchFilter((prev: any) => {
        if (prev[editMeasureColorIndex] !== undefined) {
          return prev;
        }

        return {
          ...prev,
          [editMeasureColorIndex]: {
            ...searchFilter,
            filterIndex: editMeasureColorIndex, // optional but usually correct
          },
        };
      });
    } else {
      measurementArray = [...measurementArray.map(
        (measurement: Measurement): Measurement => {
          if ((measurement.unique_id === updatedMeasurement.unique_id)) {
            measurement =  {
              ...measurement,
              isAddNew: false,
              color_criteria: updatedMeasurement.color_criteria
                ?.map((colorCriteria: ColorCriteria) => {
                  return ({
                    ...colorCriteria,
                    range_min:  colorCriteria.range_min,
                    range_max:  colorCriteria.range_max,
                    isVisible: colorCriteria.isVisible
                  });
                })
            }
          }
          return measurement;
        }
      )]
    }
    setMeasurements(measurementArray);
    // Clear any saved snapshot since changes were applied
    prevModalStateRef.current = null;
  };

  const handleApplyPossibleValuesChange = (updatedMeasurement: Measurement) => (
    event: React.FormEvent
  ) => {
    event.preventDefault();
    setShowEditPossibleValuesModal(false);
    setSelectedMeasurement(undefined);
    setMeasurements((measurements) =>
      measurements.map(
        (measurement: Measurement): Measurement => {
          if (measurement.column_name === updatedMeasurement!.column_name) {
            return updatedMeasurement;
          }
          return measurement;
        }
      )
    );
  };

  const handleApplyPossibleValuesModalHide = () => {
    setShowEditPossibleValuesModal(false);
    setSelectedMeasurement(undefined);
  };

  const handleEditColorCriteriaModalOpen = (measurement: Measurement) => () => {
    // Save a deep-cloned snapshot of current measurements and the specific searchFilter entry
    try {
      prevModalStateRef.current = {
        measurements: _.cloneDeep(measurements),
        searchFilterEntry: _.cloneDeep(searchFilter[measurement.unique_id!]),
      };
    } catch (e) {
      // fallback: do not block modal open if clone fails
      prevModalStateRef.current = null;
    }
    if(useGradeSpecificColor) {
      let searchFilterObj: any = searchFilter[measurement.unique_id!];
      if (props.mode === 'view' || props.mode === 'edit') {
        // Prepare filterGrade and selectForGrade from color_criteria structure
        let filterGrade: any[] = [];
        let selectForGrade: Record<string, { grade: string; value: string, label: string }[]> = {};
        if (measurement.custom_color_criteria) {
          
          if(!Array.isArray(measurement.custom_color_criteria) && !measurement.color_criteria?.length) {
            Object.entries(measurement.custom_color_criteria).forEach(([grade, periods]) => {
              filterGrade.push({
                label: grade,
                value: grade
              });
              selectForGrade[grade] = [];
              Object.keys(periods as object).forEach((time_period: any) => {
                if(time_period) {
                  selectForGrade[grade].push({ grade, value: time_period, label: getTimePeriodSortLabel(time_period) });
                }
              });
            });
            measurement.color_criteria = transformCustomColorCriteria(measurement.custom_color_criteria);
          } else {

            filterGrade = _.uniqBy(
              measurement.color_criteria
                ?.filter((cc) => cc.range_min !== undefined && cc.range_max !== undefined)
                ?.map((cc) => ({
                  label: cc.grade,
                  value: cc.grade,
                })) || [],
              "value"
            );
            selectForGrade = measurement.color_criteria
              ?.filter((cc) => cc.range_min !== undefined && cc.range_max !== undefined)
              ?.reduce((acc: any, cc: ColorCriteria) => {
                if (cc.grade && cc.time_period) {
                  // Only add if not already present for this grade and time_period
                  const exists = (acc[cc.grade] || []).some(
                    (item: any) => item.value === cc.time_period
                  );
                  if (!exists) {
                    if (!acc[cc.grade]) {
                    acc[cc.grade] = [];
                  }
                  acc[cc.grade].push({
                    grade: cc.grade,
                    value: cc.time_period,
                    label: getTimePeriodSortLabel(cc.time_period),
                  });
                }
              }
              return acc;
            }, {} as Record<string, { grade: string; value: string; label: string }[]>) || {};
          }

          searchFilterObj =  {
            ...searchFilterObj,
            filterIndex: measurement.unique_id!,
            filterGrade,
            // Determine whether selected grades use 4 benchmark levels.
            // For multiple selected grades the previous check (total length === 4) failed when counts summed > 4.
            filterAdditionalBenchmark: (() => {
              if (!filterGrade || filterGrade.length === 0) return false;
              // For each selected grade, count how many color criteria have defined ranges
              const counts = filterGrade.map((fg: any) => {
                return (
                  measurement.color_criteria?.filter((cc) =>
                    cc.range_min !== undefined &&
                    cc.range_max !== undefined &&
                    cc.grade === fg.value
                  ).length || 0
                );
              });
              // Treat as 4-level if every selected grade has exactly 4 defined criteria
              return counts.length > 0 && counts.every((c: number) => c === 4);
            })(),
             ...gradeOrder.reduce((acc, grade) => {
               if(selectForGrade[grade] && Array.isArray(selectForGrade[grade])) {
                 acc[`selectFor${grade}`] = selectForGrade[grade];
               }
               return acc;
             }, {} as any),
           }
        }
      }  
  

      let mergedTimePeriod = [
        ...["K", ...Array.from({ length: 12 }, (_, i) => `G${i + 1}`)]
        .flatMap((grade) => searchFilterObj?.[`selectFor${grade}`] || [])
      ];
      measurement = {
        ...measurement,
        color_criteria: measurement.color_criteria
          ?.map((colorCriteria: ColorCriteria) => {
            if(mergedTimePeriod.length && searchFilterObj.filterGrade.length) {
            let isVisibleCheck = mergedTimePeriod.some(
                  (timePeriod: any) =>
                    timePeriod.grade == colorCriteria.grade
                  && timePeriod.value == colorCriteria.time_period
                );
              return ({
                ...colorCriteria,
                range_min:  isVisibleCheck ? colorCriteria.range_min : undefined,
                range_max:  isVisibleCheck ? colorCriteria.range_max : undefined,
                isVisible: isVisibleCheck
              });
            } else {
              return {
                ...colorCriteria,
                range_min: undefined,
                range_max: undefined,
                isVisible: true
              };
            }
          })
      }
      setSearchFilter((prev: any) => {
        if(props.mode === 'view' || props.mode === 'edit') {
          return {
            ...prev,
            [measurement.unique_id!]: {
              ...prev[measurement.unique_id!],
              ...searchFilterObj,
              filterIndex: measurement.unique_id!, // optional but usually correct
            },
          };
        }
        if (prev[measurement.unique_id!] !== undefined && mergedTimePeriod.length && searchFilterObj.filterGrade.length) {
          return prev;
        }

        return {
          ...prev,
          [measurement.unique_id!]: {
            ...initialSearchObj,
            filterIndex: measurement.unique_id!, // optional but usually correct
          },
        };
      });
      if(measurement.color_criteria?.length) { // when check/uncheck the userSpecified color criteria
        if(measurement.color_criteria.filter(c => c.isVisible).every((c) => (c.range_min == undefined && c.range_max == undefined))) {
          measurement = {
            ...measurement,
            isAddNew: true,
          }

          setSearchFilter((prev: any) => {
            return {
              ...prev,
              [measurement.unique_id!]: {
                ...initialSearchObj,
                filterIndex: measurement.unique_id!,
              },
            };
          });
        }
      }
    } else {
      measurement = {
        ...measurement,
        color_criteria: measurement.color_criteria
          ?.map((colorCriteria: ColorCriteria) => {
              return {
                ...colorCriteria,
                isVisible: true
              };
          })
        }
        if(!measurement.color_criteria?.length) { // when check/uncheck the withoutReadingAlgorithm color criteria
          measurement = {
            ...measurement,
            isAddNew: true,
          }
        }
      }
      
    setEditMeasureColorIndex(measurement.unique_id!);
    setSelectedMeasurement(measurement);
    setShowEditMeasureColorCriteriaModal(true);
  };

  const handleEditColorCriteriaModalHide = () => {
    // If there is a saved snapshot, restore measurements and searchFilter (this happens on Cancel)
    if (prevModalStateRef.current) {
      // Restore measurements
      setMeasurements(prevModalStateRef.current.measurements);

      // Restore the single searchFilter entry for the edit index
      setSearchFilter((prev: any) => {
        const copy = { ...prev };
        const key = editMeasureColorIndex;
        if (prevModalStateRef.current && prevModalStateRef.current.searchFilterEntry !== undefined) {
          copy[key] = prevModalStateRef.current.searchFilterEntry;
        } else {
          // remove entry if there was none before
          delete copy[key];
        }
        return copy;
      });

      // clear snapshot
      prevModalStateRef.current = null;
    }

    // Clear modal state
    setShowEditMeasureColorCriteriaModal(false);
    setSelectedMeasurement(undefined);
    setEditMeasureColorIndex(0);
  };

  const unfulfilledMeasurement = (measurement: Measurement): boolean => {
    if (!measurement.display_name || !measurement.column_name) return true;
    if(withReadingAlgorithm && props.enterDataManuallyType == "academic") {
      return !measurement.reading_skill;
    }
    if (measurement.type === MeasurementType.CATEGORY) {
      return (
        !measurement.possible_values || measurement.possible_values.length === 0
      );
    } else {
      return (
        measurement.range_min === undefined ||
        measurement.range_max === undefined
      );
    }
  };

  const areas = useSelector<ApplicationState, SkillFocusArea[]>(
    (s) => s.groups.skillFocusInfo
  );
  const skills = useMemo(() => {
    return useGradeSpecificColor
      ? areas.flatMap(
          (a) =>
            a.skill_focuses?.map((sf) => ({
              label: sf.display_name,
              value: sf.identifier,
            })) ?? []
        )
      : [];
  }, [areas, useGradeSpecificColor]);

  const skill_areas = useMemo(() => {
    return useGradeSpecificColor
      ? areas?.map((sf) => ({
          label: sf.display_name,
          value: sf.identifier,
        })) ?? []
      : [];
  }, [areas, useGradeSpecificColor]);

  // const isHeaderNameSame = useMemo(() => {

  //   return studentLNameColumnHeader && (studentLNameColumnHeader == studentFNameColumnHeader);
  // }, [studentLNameColumnHeader, studentFNameColumnHeader]);

  const isRequiedBasicInfoFilled = useMemo(() => {
    return measurements.length > 0 
    && measurements.every((m) => !unfulfilledMeasurement(m))
    && (measurements.every((measurement) => measurement.color_criteria?.length))
    && (measurements.every((measurement) =>
        measurement.color_criteria
          ?.some((c) => c.range_min != undefined && c.range_max != undefined)))
  }, [assessmentName, measurements]);

  const isRequiedColorCriteriaFilled = () => {
    return measurements.length > 0
      && (measurements.every((measurement) => measurement.color_criteria?.length))
      && (measurements.every((measurement) =>
          measurement.color_criteria
            ?.some((c) => c.range_min != undefined && c.range_max != undefined)))
  }; 

  const refreshColorCriteria = (checked: boolean) => {
    setMeasurements((measurements) =>
      measurements.map((m) => {
        return {
          ...m,
          ...(m.type === MeasurementType.CATEGORY
            ? raBenchmarkForCategoryType
            : { color_criteria: checked ? raBenchmarkForNumericType : [] }),
        };
      })
    );
  };

  // const handleSearchFilter = (selectedSearchFilter:any) => {
  //   if(searchFilter.some((search:any)=> search.filterIndex === selectedSearchFilter.filterIndex)) {
  //     let copyObj = {...selectedSearchFilter};
  //     searchFilter[copyObj.filterIndex] = copyObj;
  //     setSearchFilter(searchFilter);
  //     setSelectedSearchFilter(searchFilter[copyObj.filterIndex]);
  //   } 
  // }

  

  return (
    <Modal
      animation={false}
      size="lg"
      style={{ backgroundColor: "rgb(255,255,255,0.8)" }}
      backdropClassName="customDarkModalBackdrop in"
      show={props.showModal}
      onShow={handleModalShow}
      onHide={() => handleHideAddAssessmentModal()}
      backdrop="static"
      keyboard={false}
      centered
    >
      <Modal.Header closeButton className="purpleModalHeader">
        <Modal.Title>
          {props.isNewProgressMonitoring 
            ? `Add Progress Monitoring Assessment​`
            : `${isAssessmentEditMode && props.mode === 'edit' ? 'Edit': props.mode === 'view' ? 'View' : 'Create'} Custom Assessment`}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {props.isNewProgressMonitoring 
            ? <h2 className="font-weight-semibold pb-4">
              Create your own assessment to monitor student goal progress​​</h2>
            : ''}
        <Form id={"assessmentBasicInfo"} onSubmit={
                                          props.isNewProgressMonitoring 
                                          ? handleCreateProgressMonitoringAssessment
                                          : props.evidenceAssessment
                                            ? handleUpdateEvidenceAssessment
                                            : handleCreateAssessment}>
        {props.isNewProgressMonitoring ? 
          <>
            <Form.Group controlId="assessmentName">
              <Form.Label className="font-weight-bold text-left">
                Assessment Name {props.mode !== 'view' ? <span className="label-asterisk mr-1">*</span> : null}
              </Form.Label>
              <Form.Control
                required
                value={assessmentName}
                onChange={handleAssessmentNameChange}
                name={"assessmentName"}
                type="text"
                placeholder="Name your assessment (e.g. SAT, Behavior Score)"
              />
              <div className="text-muted small mt-2 text-right">
                {assessmentName.length}/{MAX_LENGTH_TEXT_INPUT}
              </div>
            </Form.Group>
          </>
        : 
          <>
          {props.mode === 'view' && (
            <div className="text-center">
              <h3 className="mb-3 d-flex justify-content-center align-items-start" style={{gap: "0.5rem"}}>
                <FontAwesomeIcon 
                  icon={faLock}
                /> This is read-only mode.
              </h3>
            </div>
          )}
          <Form.Group controlId="assessmentName mb-0">
              <Form.Label className="font-weight-bold text-left">
                Assessment Name{props.mode !== 'view' ? <span className="label-asterisk mr-1">*</span> : null}
              </Form.Label>
              <Form.Control
                required
                value={assessmentName}
                onChange={handleAssessmentNameChange}
                name={"assessmentName"}
                type="text"
                placeholder="Enter"
                disabled={props.mode === 'view'}
              />
              <div className="text-muted small mt-2 text-right">
                {assessmentName.length}/{MAX_LENGTH_TEXT_INPUT}
              </div>
            </Form.Group>
            <div className="border border-secondary p-4 mb-4">
              <>
                <Form.Check
                  id="use_grade_specific_color"
                  className="mb-1 font-weight-bold"
                  checked={useGradeSpecificColor}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    const hasColorCriteria = measurements.some(
                      (m) => Array.isArray(m.color_criteria) 
                      && m.color_criteria.length > 0
                      && m.color_criteria.some((cc) => cc.range_min !== undefined && cc.range_max !== undefined)
                    );
                    if (hasColorCriteria) {
                      dispatch(
                        showConfirmDialog({
                          title: "Update Selection",
                          text: "Changing the selection will reset the applied color criteria.",
                          onConfirm: () => {
                            refreshColorCriteria(checked);
                            setUseGradeSpecificColor(checked);
                            if (!checked) setWithReadingAlgorithm(false);
                          },
                          confirmButtonText: "Proceed",
                        })
                      );
                    } else {
                      refreshColorCriteria(checked);
                      setUseGradeSpecificColor(checked);
                      if (!checked) setWithReadingAlgorithm(false);
                    }
                  }}
                  label="Use Grade and Time Period Specific Color Criteria"
                  custom
                  type="checkbox"
                  disabled={props.mode === 'view' || props.mode === 'edit'}
                />
                <p className="pl-4 assessmentCopyText">
                 When enabled, color criteria applied separately for each grade and time period.
                </p>
                {(props.enterDataManuallyType == "academic") &&  useGradeSpecificColor == false && (
                  <div className="pl-4 mt-2"><span className="alert alert-warning p-1" role="alert">
                    <strong>Note:</strong> Benchmarks will apply to all grades and time periods.
                  </span></div>
                )}
              </>
              {useGradeSpecificColor ?
                <>
                  <hr/>
                  <Form.Check
                    id="with_reading_algorithm"
                    className="mb-1 font-weight-bold"
                    checked={withReadingAlgorithm}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setWithReadingAlgorithm(checked);
                      if(!checked) {
                        setMeasurements((measurements) =>
                        measurements.map((measure) => {
                          return {
                            ...measure,
                            reading_skill: ""
                          }
                        }
                        )
                        );
                      }
                    }}
                    label={`Use with ${props.enterDataManuallyType == 'behavior' ? 'Behavior' : 'Reading'} Algorithm`}
                    custom
                    type="checkbox"
                     disabled={props.mode === 'view' || props.mode === 'edit'}
                  />
                  <p className="pl-4 assessmentCopyText">
                    When enabled, this assessment will use the ReadyCoach {props.enterDataManuallyType == 'behavior' ? 'behavior' : 'reading'} algorithms.
                    {props.enterDataManuallyType == 'academic' ? 'You`ll be asked to map your assessment’s data columns to reading skills in the table below.' : ''}
                  </p>
                </>
              : ''
              }
            </div>
          </>
        }
        </Form>
        {(!props.isNewProgressMonitoring && !isAssessmentEditMode) || (props.mode === 'view' || props.mode === 'edit') ? 
          <>    
            <h6 className="font-weight-bold">Data columns</h6>
            <hr className="m-1" />
            <Form
              id="newMeasurementForm"
              onSubmit={handleMeasurementAdd}
              ref={dataColumnFormEl} // required to confirm the parental form
            >
              <Table className="selectDataColumnTable">
                <thead>
                  <tr>
                    {/* <th>#</th> */}
                    <th style={{width: withReadingAlgorithm && (props.enterDataManuallyType == "academic") ? "20%": "25%"}}>
                      Column Name 
                        {(props.mode !== 'view') && (props.mode !== 'edit')? <span className="label-asterisk mr-1">*</span> : null}
                        <small> (e.g. PSF) </small>
                        <OverlayTrigger
                          trigger={"hover"}
                          placement="top"
                          overlay={
                            <Popover id={"popover-column-name"}>
                              <Popover.Content>Displayed on the column header in the data period.</Popover.Content>
                            </Popover>
                          }
                        >
                          <FontAwesomeIcon icon={faInfoCircle} className="align-middle" />
                        </OverlayTrigger>
                      </th>
                      <th style={{width: withReadingAlgorithm && (props.enterDataManuallyType == "academic") ? "35%": "45%"}}>
                        Display Name 
                        {(props.mode !== 'view') && (props.mode !== 'edit') ? <span className="label-asterisk mr-1">*</span> : null}
                        <small> (e.g. Phonemic Segmentation Fluency) </small>
                        <OverlayTrigger
                          trigger={"hover"}
                          placement="top"
                          overlay={
                            <Popover id={"tooltip-top-display"}>
                              <Popover.Content>Displayed on the hover of the <strong>Column Name</strong> in the data period.</Popover.Content>
                            </Popover>
                          }
                        >
                          <FontAwesomeIcon icon={faInfoCircle} className="align-middle"/>
                        </OverlayTrigger>
                      </th>
                    {/* <th>Type</th> */}
                    {withReadingAlgorithm && <th className="px-1" style={{width: "15%"}}>Reading Areas {(props.mode !== 'view') && (props.mode !== 'edit') ? <span className="label-asterisk">*</span> : null}</th>}
                    <th style={{width: "20%"}} className="px-1">Score Range{(props.mode !== 'view') && (props.mode !== 'edit') ? <span className="label-asterisk">*</span> : null}</th>
                    <th style={{width: "20%"}} className="px-1">Color Criteria{(props.mode !== 'view') && (props.mode !== 'edit') ? <span className="label-asterisk">*</span> : null}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {measurements.map((measurement, index) => {
                    if((measurement.color_criteria !== undefined) 
                    && (newMeasurement.color_criteria !== undefined) 
                    && (measurement.color_criteria?.length < newMeasurement.color_criteria?.length)) {
                      newMeasurement.color_criteria!.filter((element) => {
                        
                        if(!measurement.color_criteria!.find((data)=> data.grade == element.grade))  {
                          measurement.color_criteria!.push(element)
                        }
                        
                      });
                    }
                    return (<tr key={index}>
                      {/* <td>{index + 1}</td> */}
                      <td>
                        <Form.Control
                          required
                          name="column_name"
                          value={measurement.column_name}
                          onChange={handleMeasurementChange(index)}
                          placeholder="Enter"
                          style={{ fontSize: "0.8rem" }}
                          disabled={props.mode === 'view' || props.mode === 'edit'}
                        />
                      </td>
                      <td>
                        <Form.Control
                          required
                          name="display_name"
                          value={measurement.display_name}
                          onChange={handleMeasurementChange(index)}
                          placeholder="Enter"
                          style={{ fontSize: "0.8rem" }}
                          disabled={props.mode === 'view' || props.mode === 'edit'}
                        />
                      </td>
                      {withReadingAlgorithm && (props.enterDataManuallyType == "academic") && (
                        <td style={{ width: "190px" }}>
                          <ReadingSkillSelector
                            value={measurement.reading_skill || measurement.skill_areas?.[0] || ""}
                            skills={skill_areas}
                            onChange={(skill) => {
                              setMeasurements((measurements) =>
                                measurements.map((m, mIndex) =>
                                  index === mIndex
                                    ? { ...m, reading_skill: skill }
                                    : m
                                )
                              );
                            }}
                            isDisabledOnly={props.mode === 'view' || props.mode === 'edit'}
                          />
                        </td>
                      )}
                      <td>{createRangeTd(measurement, index)}</td>
                      <td>
                        
                          {Array.isArray(measurement.color_criteria) && measurement.color_criteria.some((c) =>
                              c.range_min != undefined && c.range_max != undefined
                            ) ? (
                              <OverlayTrigger
                                trigger={"hover"}
                                placement="top"
                                overlay={
                                  <Tooltip className="customTooltip" id="tooltip-top-display">
                                    {props.mode === 'view' ? 'View color criteria.' : props.mode === 'edit' ? 'Edit color criteria.' : 'Changing the score range will reset the color criteria.'}
                                  </Tooltip>
                                }
                              >
                                <Button
                                  variant="link"
                                  size="sm"
                                  disabled={unfulfilledMeasurement(measurement)}
                                  onClick={handleEditColorCriteriaModalOpen(measurement)}
                                  style={{ color: "#007bff" }} // override default link color
                                >{props.mode === 'view' ? 'View' : 'Edit'}</Button>
                              </OverlayTrigger>
                            ) : (
                              <Button
                                variant="link"
                                size="sm"
                                disabled={unfulfilledMeasurement(measurement)}
                                onClick={handleEditColorCriteriaModalOpen(measurement)}
                                style={{ color: "#007bff" }}
                              >Add</Button>
                            )}
                      </td>
                      <td>
                        {(index > 0) && (props.mode !== 'view') && (props.mode !== 'edit') ? <Button onClick={handleMeasurementRemove(measurement)}>
                          <FontAwesomeIcon
                            icon={faTimesCircle}
                            size="lg"
                            className="pointer ml-2"
                            style={{color: 'red'}}
                          />
                        </Button> : ''}
                      </td>
                    </tr>)
                  })}
                </tbody>
              </Table>
            </Form>
            {(props.mode !== 'view') && (props.mode !== 'edit') && 
              <div className="leftActions d-flex flex-column align-items-start my-3">
                <Button
                  type="submit"
                  form="newMeasurementForm"
                  bsPrefix="b"
                  className="blueBtnSm"
                  disabled={!isRequiedBasicInfoFilled}
                >
                  <FontAwesomeIcon icon={faPlusCircle} className="mr-1" style={{ color: "white", marginRight: "6px" }} />
                  Add Another Column
                </Button>
              </div> 
            }     
            <hr className="narrowMargin" />
            {showEditMeasureColorCriteriaModal && <EditMeasureColorCriteriaModal
              withGrades
              useGradeSpecificColor={useGradeSpecificColor}
              withReadingAlgorithm = {withReadingAlgorithm}
              onApplyChanges={handleApplyColorCriteriaChanges}
              onHide={handleEditColorCriteriaModalHide}
              measurement={selectedMeasurement}
              showModal={showEditMeasureColorCriteriaModal}
              searchFilter={searchFilter[editMeasureColorIndex]}
              onHandleSearchFilter={(updatedSearchFilter: any) => setSearchFilter((prev: any) => ({ ...prev, [editMeasureColorIndex]: updatedSearchFilter }))}
              modes={props.mode}
            />}

            <EditPossibleValuesModal
              onApplyChanges={handleApplyPossibleValuesChange}
              onHide={handleApplyPossibleValuesModalHide}
              measurement={selectedMeasurement}
              showModal={showEditPossibleValuesModal}
            />
          </>
        : ''}
          
        {props.mode !== 'view' ? (
          <div className="btnActions" style={{ marginTop: "20px" }}>
            <button className="whiteBtnSm" onClick={() => handleHideAddAssessmentModal()}>
                Cancel
            </button>
            <Button
              type="submit"
              form="assessmentBasicInfo"
              bsPrefix="b"
              className="blueBtnSm"
              disabled={(!assessmentName.trim().length || props.isLoading.updateAssessment || !isAssessmentEditMode)
                &&  (props.isLoading.createAssessment || !isRequiedBasicInfoFilled)}
            >
              { props.isNewProgressMonitoring 
                ? `Create Assessment and set goal in next step`
                : `${isAssessmentEditMode ? 'Update' : 'Create'}`
              }{" "}
              {props.isLoading.createAssessment
              || props.isLoading.updateAssessment 
              && (
                <Spinner animation="border" size="sm" />
              )}
            </Button>
          </div>
        ): (
          <div className="text-center" style={{ marginTop: "20px" }}>
            <Button
              onClick={() => handleHideAddAssessmentModal()}
              bsPrefix="b"
              className="blueBtnSm pointer"
            >
              Close
            </Button>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

const mapStateToProps = ({ onboarding }: ApplicationState): PropsFromState => {
  return {
    showModal: onboarding.modalsState.createAssessmentModal,
    enterDataManuallyType: onboarding.modalsState.enterDataManuallyType,
    isNewProgressMonitoring: onboarding.modalsState.isNewProgressMonitoring,
    isRedirectToSetGoal: onboarding.modalsState.isRedirectToSetGoal,
    evidenceAssessment: onboarding.modalsState.evidenceAssessment,
    mode: onboarding.modalsState.mode,
    fromPage: onboarding.modalsState.fromPage,
    isLoading: {
      createAssessment: onboarding.isLoading.createAssessment,
      updateAssessment: onboarding.isLoading.updateAssessment
    },
    errors: {
      createAssessment: onboarding.errors.createAssessment,
      updateAssessment: onboarding.errors.updateAssessment
    },
  };
};

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps =>
  bindActionCreators(
    {
      hideAddAssessmentModal: hideAddAssessmentModal,
      hideAddProgressMonitorModal: hideAddProgressMonitorModal,
      openAddAssessmentModal: openAddAssessmentModal,
      createAssessment: createAssessment,
      updateAssessment: updateAssessment
    },
    dispatch
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CreateAssessmentModal);